import {
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ZiroIcons } from '../models/ziro-icons.enum';
import { ZiroExplorer, ZiroExplorerColumn, ZiroExplorerNode, ZiroExplorerSubchart } from './explorer-models';
import { ExplorerLinkClickedEvent, ExplorerNodeComponent } from './explorer-node/explorer-node.component';
import { ActivatedRoute, EventType, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { PipesModule } from '../pipes/pipes.module';
import { Subscription } from 'rxjs';

interface ZiroExplorerDisplayedSubchart {
  id: string;
  columns: ZiroExplorerColumn[];
  supercolumns: ZiroExplorerColumnGroup[];
}

interface ZiroExplorerColumnGroup {
  alertForNoRows?: string;
  columnIndexes: number[];
  rows: ZiroExplorerRow[];
}

interface ZiroExplorerRow {
  group: string;
  columns: ZiroExplorerNode[][];
}

@Component({
  selector: 'ziro-explorer',
  imports: [CommonModule, TranslateModule, ExplorerNodeComponent, PipesModule],
  templateUrl: 'explorer.component.html',
  styleUrls: ['explorer.component.scss'],
})
export class ExplorerComponent implements OnInit, OnDestroy {
  readonly ZiroIcons = ZiroIcons;

  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _subscriptions = new Subscription();

  readonly config = input.required<ZiroExplorer>();
  readonly routerEvent = toSignal(this._router.events.pipe(filter((event) => event.type === EventType.NavigationEnd)));
  readonly displayedSubchart = computed<ZiroExplorerDisplayedSubchart>(() => {
    const chartId = this._route.snapshot.queryParams.subchart;
    this.routerEvent(); // listen to events
    const ziroExplorerSubchart = this.config().subcharts.find((subchart) => subchart.id === chartId);
    return this._mapToDisplayedSubchart(ziroExplorerSubchart);
  });
  readonly lastViewedSubchartId = signal<string>(null);

  readonly activeGreetingAudioUrl = input<string>();
  readonly activeGreetingAudioLoadingUrl = input<string>();
  readonly activeGreetingAudioDownloadUrl = input<string>();
  readonly playGreetingAudio = output<string>();
  readonly stopGreetingAudio = output<void>();
  readonly downloadGreetingAudio = output<string>();

  @ViewChild('explorer') explorerElement: ElementRef;
  readonly ANIMATION_DELAY = 200;

  ngOnInit(): void {
    if (!this._route.snapshot.queryParams.subchart) {
      this.linkClicked({ subchartId: this.config().subcharts[0].id });
    }

    const routeSub = this._route.queryParams.subscribe(() => {
      const displayedSubchart = this.displayedSubchart();
      if (displayedSubchart) {
        this.lastViewedSubchartId.set(displayedSubchart.id);
      }
    });

    this._subscriptions.add(routeSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  linkClicked(event: ExplorerLinkClickedEvent): void {
    setTimeout(() => {
      const displayedSubchart = this.displayedSubchart();
      if (displayedSubchart) {
        this.lastViewedSubchartId.set(displayedSubchart.id);
      }

      this._router.navigate([], {
        queryParams: { subchart: event.subchartId },
        queryParamsHandling: 'replace',
        relativeTo: this._route,
      });
    }, this.ANIMATION_DELAY);

    if (event.direction) {
      this.swoosh(event.direction);
    }
  }

  swoosh(direction: 'previous' | 'next'): void {
    const oppositeDirection = direction === 'previous' ? 'next' : 'previous';
    const explorerDiv: Element = this.explorerElement.nativeElement;

    explorerDiv.classList.add(`swoosh-${direction}`);
    setTimeout(() => {
      explorerDiv.classList.add(`swoosh-${oppositeDirection}`);
      explorerDiv.classList.remove(`swoosh-${direction}`);
      setTimeout(() => {
        explorerDiv.classList.remove(`swoosh-${oppositeDirection}`);
      }, this.ANIMATION_DELAY);
    }, this.ANIMATION_DELAY);
  }

  isFillerNodeNeeded(nodes: ZiroExplorerNode[]): { linkBefore: boolean; linkAfter: boolean } {
    const lastNode = nodes[nodes.length - 1];
    return {
      linkBefore: lastNode?.beforeConnection === 'downward',
      linkAfter: lastNode?.afterConnection === 'downward',
    };
  }

  private _mapToDisplayedSubchart(subchart: ZiroExplorerSubchart): ZiroExplorerDisplayedSubchart {
    if (!subchart) {
      return null;
    }
    const supercolumns: ZiroExplorerColumnGroup[] = [{ rows: [], columnIndexes: [0] }];
    for (let i = 1; i < subchart.columns.length; i++) {
      const columnGroups = new Set(subchart.columns[i].nodes.map((node) => node.group));
      // Check whether the previous column is grouped with this one. If it isn't, we need another supercolumn.
      if (!subchart.columns[i - 1].nodes.some((node) => columnGroups.has(node.group))) {
        supercolumns.push({ rows: [], columnIndexes: [i] });
      } else {
        supercolumns[supercolumns.length - 1].columnIndexes.push(i);
      }
    }

    supercolumns.forEach((supercolumn) => {
      supercolumn.columnIndexes.forEach((columnIndex, idx) => {
        const subchartColumn = subchart.columns[columnIndex];
        subchartColumn.nodes.forEach((node) => {
          const group = supercolumn.rows.find((row) => row.group === node.group);
          if (group) {
            group.columns[idx].push(node);
          } else {
            const groupColumns: ZiroExplorerNode[][] = new Array(supercolumn.columnIndexes.length);
            for (let i = 0; i < groupColumns.length; i++) {
              groupColumns[i] = [];
            }
            groupColumns[idx].push(node);
            supercolumn.rows.push({ group: node.group, columns: groupColumns });
          }
        });

        if (!subchartColumn.nodes.length) {
          supercolumn.alertForNoRows = subchartColumn.alertWhenNoNodesVisible;
        }
      });
    });

    return { id: subchart.id, columns: subchart.columns, supercolumns: supercolumns };
  }
}
